import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, openModal } from "./data";
import { Eyebrow, MaskLines, Reveal } from "./ui-bits";

const Services = () => {
  const [active, setActive] = useState(0);

  return (
    <section id="services" className="bg-obsidian" data-testid="services-section">
      <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <Eyebrow tone="champagne">02 / Services</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-[1.02] text-ivory sm:text-5xl lg:text-6xl">
              <MaskLines lines={["WHAT WE", "DO."]} />
            </h2>
          </div>
          <div className="flex items-end md:col-span-5">
            <Reveal delay={0.2}>
              <p className="max-w-sm text-sm leading-relaxed text-stone">
                From identifying an opportunity to closing the deal, Elevate
                stays involved throughout the journey.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          {/* Desktop editorial list */}
          <div className="hidden lg:col-span-7 lg:block">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.05}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => openModal(s.modal)}
                  className="group flex w-full items-baseline gap-8 border-t border-ivory/10 py-8 text-left last:border-b"
                  data-testid={`service-row-${s.n}`}
                >
                  <span className="font-serif text-sm italic text-champagne">
                    {s.n}
                  </span>
                  <span className="flex-1">
                    <span className="block font-serif text-3xl text-ivory transition-transform duration-500 group-hover:translate-x-2 xl:text-4xl">
                      {s.title}
                    </span>
                    <span className="mt-2 block text-[11px] uppercase tracking-[0.18em] text-stone">
                      {s.tags}
                    </span>
                  </span>
                  <span className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory/0 transition-colors duration-500 group-hover:text-champagne">
                    Explore <ArrowUpRight size={14} />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Sticky image panel */}
          <div className="hidden lg:col-span-5 lg:block">
            <div
              className="sticky top-28 relative h-[72vh] overflow-hidden"
              data-cursor="view"
              data-testid="services-image-panel"
            >
              {SERVICES.map((s, i) => (
                <motion.img
                  key={s.img}
                  src={s.img}
                  alt={s.title}
                  className="absolute inset-0 h-full w-full object-cover"
                  initial={false}
                  animate={{
                    opacity: active === i ? 1 : 0,
                    scale: active === i ? 1 : 1.05,
                  }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                />
              ))}
              <div className="absolute inset-0 bg-obsidian/20" />
              <div className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.3em] text-ivory/80">
                {SERVICES[active].n} — {SERVICES[active].title}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile horizontal cards */}
        <div
          className="-mx-5 mt-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 lg:hidden"
          data-testid="services-mobile-scroller"
        >
          {SERVICES.map((s) => (
            <button
              key={s.n}
              type="button"
              onClick={() => openModal(s.modal)}
              className="w-[78vw] shrink-0 snap-start border border-ivory/10 bg-charcoal text-left"
              data-testid={`service-card-mobile-${s.n}`}
            >
              <div className="h-44 overflow-hidden">
                <img src={s.img} alt={s.title} className="h-full w-full object-cover" />
              </div>
              <div className="p-5">
                <span className="font-serif text-xs italic text-champagne">{s.n}</span>
                <span className="mt-1 block font-serif text-2xl text-ivory">{s.title}</span>
                <span className="mt-2 block text-[10px] uppercase tracking-[0.16em] text-stone">
                  {s.tags}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
